import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setLanguage, selectLanguage } from '../../data/languageSlice';
import { getImageUrl } from '../../utils/assets';
import './HelpSettingsModal.css';

interface HelpSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const HelpSettingsModal: React.FC<HelpSettingsModalProps> = ({ visible, onClose }) => {
  const { t, i18n } = useTranslation(['game']);
  const dispatch = useAppDispatch();
  const currentLanguage = useAppSelector(selectLanguage);
  
  // Temporary state for language selection
  const [tempLanguage, setTempLanguage] = useState(currentLanguage);

  // Sync tempLanguage when modal opens or currentLanguage changes
  useEffect(() => {
    if (visible) {
      setTempLanguage(currentLanguage);
    }
  }, [visible, currentLanguage]);

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  const handleLanguageSelect = (lang: string) => {
    setTempLanguage(lang);
  };

  const handleSave = () => {
    if (tempLanguage !== currentLanguage) {
      i18n.changeLanguage(tempLanguage);
      dispatch(setLanguage(tempLanguage));
      localStorage.setItem('language', tempLanguage);
    }
    onClose();
  };

  const handleCancel = () => {
    setTempLanguage(currentLanguage);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className={`help-modal-container ${visible ? 'visible' : ''}`}>
      <div className="help-modal-backdrop" style={{ backgroundImage: getImg('st_background.png') }} onClick={onClose} />

      <div className="help-modal-content-wrapper">
        <div className="help-modal-panel-box">
          {/* Background */}
          <div className="help-modal-panel-bg" style={{ backgroundImage: getImg('st_panel.png') }} />

          {/* Title */}
          <div className="help-modal-title">{t('help.title')}</div>

          {/* Close Button */}
          <div
            className="help-modal-close-button"
            style={{ backgroundImage: getImg('st_close_button.png') }}
            onClick={handleCancel}
          />

          {/* Settings Content */}
          <div className="help-modal-settings-content">
            <div className="help-modal-language-section">
              <div className="help-modal-language-title">{t('help.languageSettings')}</div>
              <div className="help-modal-language-divider" style={{ backgroundImage: getImg('st_divider_line.png') }} />
              
              <div className="help-modal-language-options">
                {/* English */}
                <div className="help-modal-language-option" onClick={() => handleLanguageSelect('en')}>
                  <div 
                    className="help-modal-language-flag" 
                    style={{ backgroundImage: getImg('st_radio_english.png') }} 
                  />
                  <div className="help-modal-language-name">English</div>
                  <div 
                    className="help-modal-language-radio" 
                    style={{ backgroundImage: getImg(tempLanguage === 'en' ? 'st_radio_checked.png' : 'st_radio_unchecked.png') }}
                  >
                    {tempLanguage === 'en' && <div className="help-modal-check-icon" style={{ backgroundImage: getImg('st_checkbox_icon.png') }} />}
                  </div>
                </div>

                {/* Chinese */}
                <div className="help-modal-language-option" onClick={() => handleLanguageSelect('zh-TW')}>
                  <div 
                    className="help-modal-language-flag" 
                    style={{ backgroundImage: getImg('st_radio_chinese.png') }} 
                  />
                  <div className="help-modal-language-name">繁體中文</div>
                  <div 
                    className="help-modal-language-radio" 
                    style={{ backgroundImage: getImg(tempLanguage === 'zh-TW' ? 'st_radio_checked.png' : 'st_radio_unchecked.png') }}
                  >
                    {tempLanguage === 'zh-TW' && <div className="help-modal-check-icon" style={{ backgroundImage: getImg('st_checkbox_icon.png') }} />}
                  </div>
                </div>

                {/* Portuguese */}
                <div className="help-modal-language-option" onClick={() => handleLanguageSelect('pt')}>
                  <div 
                    className="help-modal-language-flag" 
                    style={{ backgroundImage: getImg('st_radio_portuguese.png') }} 
                  />
                  <div className="help-modal-language-name">Português</div>
                  <div 
                    className="help-modal-language-radio" 
                    style={{ backgroundImage: getImg(tempLanguage === 'pt' ? 'st_radio_checked.png' : 'st_radio_unchecked.png') }}
                  >
                    {tempLanguage === 'pt' && <div className="help-modal-check-icon" style={{ backgroundImage: getImg('st_checkbox_icon.png') }} />}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div 
                className="help-modal-action-btn help-modal-save-btn" 
                style={{ backgroundImage: getImg('st_language_button.png') }}
                onClick={handleSave}
              >
                <span className="help-modal-action-text">{t('common.save')}</span>
              </div>
              <div 
                className="help-modal-action-btn help-modal-cancel-btn" 
                style={{ backgroundImage: getImg('st_language_button.png') }}
                onClick={handleCancel}
              >
                <span className="help-modal-action-text">{t('common.cancel')}</span>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSettingsModal;
